import { AnyTaggedItem } from "src/types";
import { db } from "../dexie";
import { assertExists } from "../utils/assertExists";
import { Habit } from "../types";
import { Habits } from "../services/Habits";
import { updateItem } from "./items";
import { HabitInstantiator } from "../models/HabitInstantiator";
import { startOfWeek, endOfWeek, addDays } from "date-fns";
import { getLocalDate, parseLocalDate } from "../utils/localDate";
import { Sync } from "../sync";
import { Ordering } from "../models/Ordering";

export async function createHabit(habit: Habit): Promise<void> {
  await Promise.all([
    assertExists(habit.goalId, db.goals, { optional: true }),
    assertExists(habit.parentId, db.habits, { optional: true }),
  ]);
  await Sync.upsert(habit);
}

export async function deleteHabit(id: string): Promise<void> {
  await Sync.delete({ id, type: "habit" });
}

export async function getDayPlan(date: string) {
  const tasks = await HabitInstantiator.getInstancesByDate(date);
  return Ordering.toSorted(tasks, (t) => t.order);
}

export async function getTemplate(): Promise<Habit[]> {
  return await Habits.getAll().toArray();
}

/** Habit templates linked to a goal, in global template order (same basis as {@link getTemplate}). */
export async function getHabitsForGoal(goalId: string): Promise<Habit[]> {
  const all = await getTemplate();
  return all.filter((h) => h.goalId === goalId);
}

/** Reorder / reparent habits when editing a single goal's habit subtree (siblings scoped to that goal). */
export async function moveHabitWithinGoal(
  goalId: string,
  item: AnyTaggedItem,
  parent: AnyTaggedItem | null,
  order: number,
): Promise<void> {
  if (item.type !== "habit") {
    throw new Error("moveHabitWithinGoal expects a habit");
  }
  if (parent && parent.type !== "habit") {
    throw new Error("Cannot move item to a parent of a different type");
  }
  const parentId = parent ? parent.id : null;
  const siblings = await Habits.getByParentId(parentId)
    .filter((habit) => habit.id !== item.id && habit.goalId === goalId)
    .toArray();
  Ordering.sortInPlace(siblings, (h) => h.order);
  const newOrder = Ordering.insertAt(siblings, (h) => h.order, order);
  await updateItem({ ...item, order: newOrder, parentId });
}

export async function getWeekPlan(date: string) {
  const anchor = parseLocalDate(date);
  const start = getLocalDate(startOfWeek(anchor, { weekStartsOn: 1 }));
  const end = getLocalDate(endOfWeek(anchor, { weekStartsOn: 1 }));

  let results = await HabitInstantiator.getInstancesByDateRange(start, end);
  return results.map((tasks, i) => ({
    date: getLocalDate(addDays(parseLocalDate(start), i)),
    tasks,
  }));
}
